import React, { useEffect, useState } from "react"
import PaginatedView from "components/PaginatedView"
import { FormSubmissionSortOption } from "sharedTypes"
import { Box, Flex } from "@chakra-ui/react"
import MenuWithOptionGroups, { MenuWithOptionGroupType } from "components/MenuWithOptionGroups"
import Button from "components/Buttons/Button"
import { useForms } from "queries/forms"
import Spinner from "components/elements/Spinner"
import useSearchParams from "services/useSearchParams"
import { useEventFormSubmissionsPaginated } from "queries/formSubmissions"
import FormSubmissionListItem from "./FormSubmissionListItem"
import FilterPopover from "./FilterPopover/FilterPopover"
import formIdsOfType from "./formTypeFilter"

type Props = {
  eventId: string
  onPageChange: () => void
}

const FormSubmissionsListItems = ({ eventId, onPageChange }: Props) => {
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<string|undefined>()
  const [formIdFilters, setFormIdFilters] = useState<string[]>([])
  const [statusFilters, setStatusFilters] = useState<string[]>([])
  const { isLoading: formsIsLoading, data: forms, isError: formsFailed } = useForms(eventId)
  const { formId, formType } = useSearchParams()
  // A dashboard link to one form type: wait for the forms to filter by, then show only those.
  const [typeFilter, setTypeFilter] = useState<"pending" | "none" | "applied" | undefined>(
    !formId && formType ? "pending" : undefined,
  )
  const setFormIds = (ids: string[]) => {
    setTypeFilter(undefined)
    setFormIdFilters(ids)
  }

  const handleChangeSort = (selectedSortOption: string) => setSort(selectedSortOption)
  const handleResetFilters = () => {
    setFormIds([])
    setStatusFilters([])
  }
  const handlePageChange = (newPage: number) => {
    setPage(newPage)
    onPageChange()
  }

  useEffect(() => {
    if (formId) {
      setFormIdFilters([formId])
    }
  }, [])

  useEffect(() => {
    if (typeFilter === "pending" && formsFailed) setTypeFilter(undefined)
  }, [formsFailed])

  useEffect(() => {
    if (typeFilter !== "pending" || !forms) return

    const ids = formIdsOfType(forms.data, formType)
    setFormIdFilters(ids)
    setTypeFilter(ids.length ? "applied" : "none")
  }, [forms])

  const {
    data,
    isPreviousData,
    isLoading: formSubmissionsIsLoading,
  } = useEventFormSubmissionsPaginated(
    eventId,
    page,
    sort as FormSubmissionSortOption,
    formIdFilters,
    statusFilters,
    { enabled: typeFilter !== "pending" && typeFilter !== "none" },
  )

  const viewButtonGroups = [{
    groupTitle: "Sort By",
    type: MenuWithOptionGroupType.Radio,
    defaultValue: FormSubmissionSortOption.CreatedAtDesc,
    onChange: handleChangeSort,
    options: [
      { title: "Date Created (Ascending)", value: FormSubmissionSortOption.CreatedAtAsc },
      { title: "Date Created (Descending)", value: FormSubmissionSortOption.CreatedAtDesc },
      { title: "Total Ticket Price (Ascending)", value: FormSubmissionSortOption.PriceAsc },
      { title: "Total Ticket Price (Descending)", value: FormSubmissionSortOption.PriceDesc },
      { title: "Name of Guest / Ticket Buyer (A-Z)", value: FormSubmissionSortOption.GuestNameAsc },
    ],
  }]

  if (formsIsLoading) {
    return <Spinner />
  }

  if (!forms) {
    return null
  }

  const header = (
    <Flex alignItems="center" justifyContent="space-between" minH={20} px={6} backgroundColor="white">
      <Flex>
        <MenuWithOptionGroups title="Sort" optionGroups={viewButtonGroups} />
        <Box ml={6}>
          <FilterPopover
            selectedFormIdFilters={formIdFilters}
            onSelectFormIdFilters={setFormIds}
            selectedStatusFilters={statusFilters}
            onSelectStatusFilters={setStatusFilters}
            forms={forms.data}
          />
        </Box>
      </Flex>
      {(formIdFilters.length > 0 || statusFilters.length > 0 || typeFilter === "none") && (
        <Button variant="outline" colorScheme="button.light" size="sm" rightIcon="times" onClick={handleResetFilters}>
          Clear Filter
        </Button>
      )}
    </Flex>
  )

  const items = typeFilter === "none" ? [] : data?.data || []
  const totalCount = typeFilter === "none" ? 0 : data?.meta.totalCount || 0

  return (
    <PaginatedView
      Component={FormSubmissionListItem}
      entityName="Item"
      borderWidth={0}
      additionalHeader={header}
      items={items}
      totalCount={totalCount}
      isPreviousData={isPreviousData}
      page={page}
      onChangePage={handlePageChange}
      isLoading={formSubmissionsIsLoading || typeFilter === "pending"}
    />
  )
}

export default FormSubmissionsListItems
